import { Flex, InputProps, List, ListItem, Text } from '@chakra-ui/react';
import { InfiniteQueryObserverResult } from '@tanstack/react-query';
import { range } from 'lodash';
import { useRef } from 'react';
import { SearchInput } from '~/components/SearchInput';
import { useLoadingMore } from '~/hooks/useLoadingMore';
import { ICreator } from '~/types';
import { pluralize } from '~/utils';
import { convertPxToRems } from '~/utils-ts';

import {
  CreatorListItem,
  CreatorListItemSkeleton,
  CreatorListResultSkeleton,
} from './components';

interface Props {
  creators: ICreator[];
  displayResultsText?: boolean;
  fetchNextPage?: () => Promise<InfiniteQueryObserverResult<any>>;
  hasNextPage?: boolean;
  inputProps: Omit<InputProps, 'onChange' | 'defaultValue' | 'value'>;
  inputTrackId?: string;
  isFetchingNextPage?: boolean;
  itemTrackId?: string;
  onFilter: (creatorId: string) => void;
  onSelectCreator: (creator: ICreator) => void;
  pageSize?: number;
  /**
   * Temporary prop to have intermediate UI where consolidated filters drawer is able to render old creator search component
   * @default 6
   */
  paddingX?: number;

  maxListHeight?: string | undefined;
}

export const CreatorsSearch: React.FC<Props> = ({
  creators,
  displayResultsText,
  fetchNextPage,
  hasNextPage,
  inputProps,
  inputTrackId,
  isFetchingNextPage,
  itemTrackId,
  onFilter,
  onSelectCreator,
  pageSize = 30,
  paddingX = 6,
  maxListHeight,
}) => {
  const fetchNextPageRef = useRef<HTMLUListElement>(null);
  const fetchingNextPage = useLoadingMore({
    canLoadMore: hasNextPage,
    loadMore: fetchNextPage,
    root: fetchNextPageRef.current,
  });

  return (
    <Flex direction="column" width="100%" paddingX={paddingX}>
      <SearchInput
        onChange={onFilter}
        inputProps={inputProps}
        trackId={inputTrackId}
      />

      {displayResultsText && isFetchingNextPage && (
        <CreatorListResultSkeleton />
      )}

      {displayResultsText && !isFetchingNextPage && (
        <Text
          marginTop={4}
          marginLeft={2}
          as="span"
          lineHeight={convertPxToRems(16)}
          color="primary.300"
          fontSize="sm"
        >
          {creators.length} {pluralize('result', 'results', creators.length)}
        </Text>
      )}

      <List
        ref={fetchNextPageRef}
        overflowY="auto"
        marginTop={4}
        flex={1}
        maxHeight={maxListHeight}
      >
        {creators.map((creator) => (
          <ListItem
            key={creator.id}
            _notFirst={{
              marginTop: 2,
            }}
          >
            <CreatorListItem
              key={creator.id}
              creator={creator}
              onSelectCreator={onSelectCreator}
              trackId={itemTrackId}
            />
          </ListItem>
        ))}

        {isFetchingNextPage &&
          range(0, pageSize).map((i) => (
            <ListItem
              key={i}
              _notFirst={{
                marginTop: 2,
              }}
            >
              <CreatorListItemSkeleton />
            </ListItem>
          ))}

        <ListItem>{fetchingNextPage}</ListItem>
      </List>
    </Flex>
  );
};
